import React, {Component} from "react";
import {connect} from 'react-redux';
import {
	Box,
	Button,
	Chip,
	Grid,
	IconButton,
	Popover,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography
} from "@mui/material";
import {
	updateDisplaySetting,
	updateFieldDisplaySetting,
	updateSlideSetting
} from "../../../redux_actions/maps.state.action";
import {ReplayOutlined} from "@mui/icons-material";
import {getSettingComponent} from "../utils/settingsUtils";
import {BrightnessKey, ContrastKey, SettingsKeys} from "../keys/Settings";
import {Loading, LoadingText} from "../components/components";
import {delete_, get, patch, post} from "../../../helper/request";
import {error_manager} from "../../../helper/error_manager";
import {DisplaySettingType, Loader} from "../../../utils/const";
import {notifyError} from "../utils/display.notification";
import {isOwner} from "../utils/displaySettingsUtils";
import {aclPermissions, hasPermissions} from "const/ACLPermissions";
import CreateChip from "../components/CreateButton";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {setOsdFallbackFeatures} from "../../../redux_actions/gamma.state.action";
import {updateOsdUserConfig} from "../../../redux_actions/osd.viewer.config.action";
import {resolveActiveViewer, VIEWERS} from "../openseadragon/viewerSelection";

class EditableChip extends Component {
	constructor(props) {
		super(props);
		this.state = {
			editing: false,
			name: props.setting.name,
			anchorEl: null,
			isLimitReached: false
		};

		this.debounceTimer = null;
	}

	hasEditSlideSettingPermission = () => isOwner(this.props.displaySetting) && hasPermissions([aclPermissions.update_slide_setting])

	handleInputChange = (event, setting) => {
		const {value} = event.target;
		if (value.length > 7) {
			notifyError('settingName', "Display settings name should not exceed 7 chars")
			this.setState({isLimitReached: true})
			return;
		}
		this.setState({name: value, isLimitReached: false});
		let newVal = {
			name: value,
		};

		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer);
		}

		// Set a new timer to trigger the API call after 1000ms
		this.debounceTimer = setTimeout(() => {
			patch(`/api/display/settings/${setting.id}/`, newVal)
				.catch(error => error_manager(`Failed to update ${setting.name}`, error))
		}, 1000);
	};

	handleMenuOpen = (event) => {
		this.setState({anchorEl: event.currentTarget});
	};

	handleMenuClose = () => {
		this.setState({anchorEl: null, editing: false});
	};

	handleEditClick = () => {
		this.setState({editing: true});
	};

	handleDeleteClick = () => {
		this.handleMenuClose();
		this.props.onDelete(this.props.setting);
	};

	render() {
		const {selectedId, setting} = this.props;
		const {editing, name, anchorEl, isLimitReached} = this.state;
		const open = Boolean(anchorEl);

		return (
			<>
				<Chip
					className={'dispSetting'}
					label={name}
					onClick={() => {
						this.props.onClick(setting);
					}}
					onDelete={(isOwner(setting) || this.props.canDelete) && this.handleMenuOpen}
					deleteIcon={(isOwner(setting) || this.props.canDelete) && (<MoreVertIcon disableRipple sx={{backgroundColor: 'none'}}/>)}
					variant={"filled"}
					color={setting.id === selectedId ? "secondary" : "primary"}
					sx={{
						width: "30%", height: 25, margin: 0.25, borderRadius: 1, justifyContent: "space-between",
						opacity: !isOwner(setting) && setting.id !== selectedId  && !this.hasEditSlideSettingPermission() ? 0.6 : 1,
						maxWidth: '100%',
					}}
				/>
				<Popover
					open={open}
					anchorEl={anchorEl}
					onClose={this.handleMenuClose}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'center',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'center',
					}}
				>
					{editing ? <div style={{display: 'flex', alignItems: 'center'}}>
							<Typography color={"secondary"}
										sx={{textTransform: 'capitalize', marginLeft: 1, fontSize: 12, marginTop: 1}}>
								{isLimitReached ? `Can't exceed 7 chars. ` : 'Max 7 chars. accepted'}
							</Typography>
						</div>
						: <div style={{display: 'flex', alignItems: 'center'}}>
							<Button onClick={this.handleDeleteClick} color={"secondary"}
									disabled={!isOwner(setting) && !hasPermissions([aclPermissions.delete_slide_setting])}
									sx={{textTransform: 'capitalize'}}>
								{'Delete Setting'}
							</Button>
						</div>}
					{editing ? <div style={{padding: '10px', alignItems: 'center'}}>
						<TextField
							variant="outlined"
							value={name}
							onChange={(e) => this.handleInputChange(e, setting)}
							sx={{marginRight: '10px', width: 80, display: 'block', border: '0.5px solid #00ffff'}}
						/>
						<Typography variant="contained"
									color={"secondary"} sx={{height: 20, fontSize: 10, display: 'block'}}>
							{'Edit name and hit Escape to save'}
						</Typography>
					</div> : <div style={{display: 'flex', alignItems: 'center'}}>
						<Button
							onClick={this.handleEditClick}
							disabled={!isOwner(setting) && !hasPermissions([aclPermissions.update_slide_setting])}
							color={"secondary"}
							sx={{textTransform: 'capitalize'}}>
							{'Edit Setting Name'}
						</Button>
					</div>}
				</Popover>
			</>
		);
	}
}

class DisplaySettings extends Component {

	constructor(props) {
		super(props);
		this.state = this.initState();
	}

	hasEditSlideSettingPermission = () => isOwner(this.props.displaySetting) && hasPermissions([aclPermissions.update_slide_setting])


	componentDidMount() {
		this.fetchAllDisplaySettings().then(this.backup);
    }
  
	componentDidUpdate(prevProps, prevState, snapshot) {
		if (prevProps.type !== this.props.type) {
			this.setState(this.initState());
			this.backup();
		}
	}

	initState = () => {
		this.backupKey = `backupDisplaySetting${this.props.type}`;
		switch (this.props.type) {
			case DisplaySettingType.SLIDE:
				this.slideKey = "display_setting";
				this.settingKeys = SettingsKeys;
				this.updateDisplaySetting = updateDisplaySetting;
				break;
			case DisplaySettingType.FIELD:
				this.slideKey = "field_display_setting";
				this.settingKeys = [BrightnessKey, ContrastKey];
				this.updateDisplaySetting = updateFieldDisplaySetting;
				break;
			default:
				error_manager("Unknown display setting type prop received");
		}
		return {};
	}

	backup = () => {
		if (!this.props.mapState[this.backupKey] ||
			this.props.mapState[this.backupKey]?.id !== this.props.displaySetting?.id)
			this.props.mapState[this.backupKey] = {...this.state[this.props.displaySetting?.id]};
	}

	fetchAllDisplaySettings = () =>
		get(`/api/display/settings/?view=list`)
			.then(settings =>
				this.setState(
					settings.reduce((settings, setting) => Object.assign(settings, {
						[setting.id]: setting,
					}), {})
				))
			.catch(error => error_manager("Failed to fetch display settings", error))

	updateSettingStateInReducer = (displaySetting) =>
		this.props.dispatch(this.updateDisplaySetting(this.props.activeMapId, displaySetting));

	update = (setting, value) => {
		let displaySetting = {
			[setting.name.toLowerCase()]: parseInt(value),
		};
		patch(`/api/display/settings/${this.props.displaySetting.id}/`, displaySetting)
			.then(setting => this.setState({[setting.id]: setting}))
			.catch(error => error_manager(`Failed to update ${setting.name}`, error))
		this.updateSettingStateInReducer(displaySetting);
	}

	create = (name) => {
		if (name.length > 7) {
			notifyError('settingName', "Display settings name should not exceed 7 chars")
			return;
		}
		post(`/api/display/settings/`, {
			...this.props.displaySetting,
			name,
			id: undefined,
		})
			.then(setting => this.setState({[setting.id]: setting}))
			.catch(error => error_manager(`Failed to create display setting ${name}`, error));
	}

	delete = async (setting) => {
		const display_setting_param = setting.type === DisplaySettingType.SLIDE
			? "display_setting"
			: "field_display_setting"

		let otherRelatedSettings = await get(`/api/slide/settings/?${display_setting_param}=${setting.id}`)
            .catch(e => error_manager('', e))
		
		if (this.props.displaySetting.id === setting.id) {
			notifyError(setting.id,
				`Can not delete display setting "${setting.name}" as it is being used for the current slide.`)
		   return
		}

		otherRelatedSettings = 	otherRelatedSettings.filter(setting => setting.slide !== this.props.slide.id);

		if (otherRelatedSettings?.length > 0) {
			notifyError(setting.id,
				 `Can not delete display setting "${setting.name}" as it is being used in ${otherRelatedSettings?.length} slide settings`)
			return
		}

		delete_(`/api/display/settings/${setting.id}/`)
			.then(() => this.setState({[setting.id]: false}))
			.catch(error => error_manager(`Failed to delete display setting ${setting.name}`, error))
	}

	updateDisplaySettingInstance = (displaySetting) => {
		this.props.dispatch(updateSlideSetting(this.props.activeMapId,
			{
				id: this.props.slide.setting.id,
				[this.slideKey]: displaySetting.id,
			}
		));
		this.updateSettingStateInReducer(displaySetting);
	}

	restore = () =>
		this.props.mapState[this.backupKey] && this.updateSettingStateInReducer(this.props.mapState[this.backupKey]);

	getOptionRow = () =>
		<Stack direction={"row"} spacing={1} justifyContent={"space-between"}>
			<Stack direction={"row"} spacing={1}>
				<Tooltip title={"Restore"} placement={"right"}>
					<IconButton color={"secondary"} onClick={this.restore}>
						<ReplayOutlined/>
					</IconButton>
				</Tooltip>
			</Stack>
			{!isOwner(this.props.displaySetting) &&
				<Typography variant={"body2"}>You can not modify the settings since you are not the owner</Typography>}
		</Stack>;

	getChipsComponent = () => {
		let settings = Object.values(this.state).filter(setting => setting.type === this.props.type)
			.sort((setting1, setting2) => new Date(setting1.created_on) - new Date(setting2.created_on));
		let selectedId = this.props.displaySetting.id;

		return this.state[this.props.displaySetting.id] ?
			<Box maxHeight={"11.3vh"} overflow={"auto"}>
				<Grid width={"100%"}>
					{hasPermissions([aclPermissions.create_slide_setting]) &&
						<CreateChip onCreate={this.create} label={"+ new"}
									sx={{
										width: "30%",
										borderRadius: 1,
										margin: 0.25,
										borderStyle: "dashed"
									}}/>
					}
					{settings.map(setting => setting &&
						<EditableChip key={setting.id} setting={setting} selectedId={selectedId}
									  canDelete={hasPermissions([aclPermissions.delete_slide_setting])}
									  onClick={() => this.updateDisplaySettingInstance(setting)}
									  onDelete={() => this.delete(setting)}/>
					)}
				</Grid>
			</Box> : <Loading/>;
	}

	selectViewer = (viewer) => {
		this.props.dispatch(setOsdFallbackFeatures([]));
		this.props.dispatch(updateOsdUserConfig({preferred_viewer: viewer}));
	}

	/** Superusers only: their own V1/V2 choice, saved to viewer_configurations.osd.preferred_viewer. */
	getViewerToggle() {
		if (this.props.type === DisplaySettingType.FIELD || !this.props.isSuperuser || !this.props.activeViewer)
			return null;
		return <Box>
			<Typography variant="caption" sx={{color: 'whitesmoke', display: 'block', mb: 0.5}}>Viewer</Typography>
			<ToggleButtonGroup
				value={this.props.activeViewer}
				exclusive
				onChange={(e, value) => value && value !== this.props.activeViewer && this.selectViewer(value)}
				size="small"
				fullWidth
				sx={{
					'& .MuiToggleButton-root': {
						color: 'rgba(255,255,255,0.85)',
						py: 0.5,
						textTransform: 'none',
						fontSize: '0.8rem',
					},
					'& .MuiToggleButton-root.Mui-selected': {
						color: '#00f548',
						backgroundColor: 'rgba(28, 0, 46, 0.95)',
					},
				}}
			>
				<ToggleButton value={VIEWERS.OL}>V1 (OpenLayers)</ToggleButton>
				<ToggleButton value={VIEWERS.OSD}>V2 (OpenSeadragon)</ToggleButton>
			</ToggleButtonGroup>
		</Box>
	}

	render() {
		if (this.props.loadingState.loading) {
			return <Loader/>
		}
		if (!this.props.displaySetting)
			return <LoadingText/>;
		return <>
			<Stack direction={"column"} spacing={1}>
				{this.getViewerToggle()}
				{this.getChipsComponent()}
				{this.getOptionRow()}
				{this.settingKeys.map(setting => getSettingComponent(setting,
					this.props.displaySetting[setting.name.toLowerCase()], value => this.update(setting, value),
					!this.hasEditSlideSettingPermission(), null, "sliderDefaultColor"))}
			</Stack>
		</>;
	}
}

export const mapStateToProps = (state, ownProps) => {
	let loadingState = state.slideLoadingReducer;
	let {activeMapId} = state.gammaStateReducer;
	let mapState = state.mapsStateReducer[activeMapId] ?? {};
	let {slide, displaySetting, fieldDisplaySetting} = state.mapsStateReducer[activeMapId] ?? {};
	if (ownProps.type === DisplaySettingType.FIELD)
		displaySetting = fieldDisplaySetting;

	return {
		activeMapId, mapState, slide, displaySetting, loadingState,
		isSuperuser: !!state.osdViewerConfigReducer.permissions?.is_superuser,
		activeViewer: resolveActiveViewer(state),
	};
}

export default connect(mapStateToProps)(DisplaySettings);
